<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :hide-save-button="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
        <BaseInput
          v-model="modifiedData.name"
          color="base"
          show-error-as-tooltip
          :error="$v.name.required.$invalid ? $v.name.required.$message : ''" />
      </div>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="code" class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
        <BaseInput
          v-model="modifiedData.code"
          color="base"
          show-error-as-tooltip
          :error="$v.code.required.$invalid ? $v.code.required.$message : ''" />
      </div>

      <BaseSelectDropdown
        :items="dropdownTypes"
        :selected-item="selectedItem"
        :disable="!modifiedData.isFromManager"
        :label="$t('pricing-catalogs.status')"
        @update:selected="handleSelection"
        name="documentStatus"
        clearable
        show-error-as-tooltip
        :error="$v.status.required.$invalid ? $v.status.required.$message : ''" />

      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager }"
        :label="$t('baseObject.isActive')"
        name="isActive"
        v-model="modifiedData.isActive" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';
import { addOrEditDocumentStatus } from '@/services/api/documents';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { enumToDropdownItems } from '@/utils/enums';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';

import { type DocumentStatus, eDocumentStatus } from '@/types/DocumentStatus';
import { type SelectDropdownItem } from '@/types/Base';
import { deepCopy } from '@/utils/data';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    documentStatus: DocumentStatus;
  }>(),
  {}
);

const modifiedData = ref<DocumentStatus>(deepCopy<DocumentStatus>(props.documentStatus));
const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.code = newData.code || '';
    validationModel.value.status = newData.statusString || '';

    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.documentStatus);
  },
  { deep: true }
);

const dropdownTypes = ref(enumToDropdownItems(eDocumentStatus));

const selectedItem = ref<SelectDropdownItem | null>(null);

function handleSelection(item: SelectDropdownItem) {
  selectedItem.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.status = item ? +item.value : null;
    modifiedData.value.statusString = item ? item.name : '';
  }
}

function onDismissChanges() {
  modifiedData.value = deepCopy<DocumentStatus>(props.documentStatus);
  emit('closeModal');
}

const toastStore = useToastStore();

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await addOrEditDocumentStatus(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: `${$t('common.toast.error')}` });
    emit('closeModal');
  }
}

onMounted(() => {
  if (modifiedData.value && modifiedData.value.status !== undefined) {
    const selectedValue = dropdownTypes.value.find(
      (item) => item.value === modifiedData.value?.status
    );
    if (selectedValue) {
      selectedItem.value = selectedValue;
    }
  }
});

const validationModel = ref({
  name: modifiedData.value.name || '',
  code: modifiedData.value.code || '',
  status: modifiedData.value.statusString || '',
});

const rules = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  code: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  status: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
